import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/atoms";

const STEPS = ["Submitted", "Parlor Review", "Crest Issued"];

export default function Membership() {
  const { user, refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ full_name: user?.name || "", phone: "", city: "", reason: "", how_heard: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/membership/me");
      setInfo(data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/membership/apply", form);
      toast.success("Application submitted");
      await refreshUser();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <Loading />;
  const status = info.status;
  const stepIndex = status === "approved" ? 3 : status === "pending" ? 2 : 1;

  if (user?.role === "admin") {
    return (
      <div className="p-6 text-center mt-8">
        <CheckCircle2 size={48} className="mx-auto text-[#8B1E28]" />
        <h2 className="font-display font-bold text-2xl mt-4 uppercase">You're Staff</h2>
        <p className="text-sm text-[#554E40] mt-2">Admins have full access to the club.</p>
      </div>
    );
  }

  return (
    <div className="p-5" data-testid="membership-page">
      <span className="eyebrow">The Club</span>
      <h1 className="font-display font-black text-3xl uppercase leading-tight mb-1">Membership</h1>

      {status === "approved" ? (
        <div className="atc-card p-6 mt-4 text-center bg-[#8B1E28] text-[#F4F0E6]" data-testid="membership-approved">
          <CheckCircle2 size={44} className="mx-auto" color="#C7A656" />
          <h2 className="font-display font-black text-2xl mt-3 uppercase">Crest Issued</h2>
          <p className="text-sm mt-2 opacity-90">You're a full member. Welcome to Alpha Tattoo Club.</p>
          {info.approved_at && <p className="font-mono2 text-xs mt-3 opacity-80">Member since {new Date(info.approved_at).toLocaleDateString()}</p>}
        </div>
      ) : (
        <>
          {/* Tracker */}
          <div className="flex items-center justify-between my-6" data-testid="membership-tracker">
            {STEPS.map((s, i) => {
              const active = i + 1 <= stepIndex && status !== "none" && status !== "rejected";
              return (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <div className={`w-9 h-9 rounded-full border-2 border-black flex items-center justify-center font-mono2 font-bold ${active ? "bg-[#8B1E28] text-[#F4F0E6]" : "bg-[#EAE4D3]"}`}>{i + 1}</div>
                    <span className="text-[10px] font-mono2 uppercase text-center">{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-[2px] flex-1 ${active ? "bg-[#8B1E28]" : "bg-black/30"}`} />}
                </React.Fragment>
              );
            })}
          </div>

          {status === "pending" && (
            <div className="atc-card p-6 text-center" data-testid="membership-pending">
              <Clock size={40} className="mx-auto" />
              <h2 className="font-display font-bold text-xl mt-3 uppercase">Under Review</h2>
              <p className="text-sm text-[#554E40] mt-2">Your application is with the parlor. Hang tight — we'll issue your crest soon.</p>
            </div>
          )}

          {status === "rejected" && (
            <div className="atc-card p-5 text-center mb-4 bg-[#EAE4D3]" data-testid="membership-rejected">
              <XCircle size={36} className="mx-auto text-[#8B1E28]" />
              <p className="text-sm mt-2 font-bold">Not approved this time. You can re-apply below.</p>
            </div>
          )}

          {(status === "none" || status === "rejected") && (
            <form onSubmit={submit} className="flex flex-col gap-3 mt-2" data-testid="membership-form">
              <input className="atc-input" placeholder="Full name" value={form.full_name} onChange={set("full_name")} required data-testid="app-fullname" />
              <input className="atc-input" placeholder="Phone" value={form.phone} onChange={set("phone")} required data-testid="app-phone" />
              <input className="atc-input" placeholder="City" value={form.city} onChange={set("city")} required data-testid="app-city" />
              <textarea className="atc-input" rows={3} placeholder="Why do you want to join?" value={form.reason} onChange={set("reason")} required data-testid="app-reason" />
              <input className="atc-input" placeholder="How did you hear about us?" value={form.how_heard} onChange={set("how_heard")} required data-testid="app-howheard" />
              <button type="submit" className="atc-btn btn-primary" disabled={busy} data-testid="app-submit">
                {status === "rejected" ? "Re-Apply" : "Submit Application"}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
